"use client";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import cn from "twsx";

const TooltipProvider = ({ delayDuration = 400, skipDelayDuration = 300, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider
    delayDuration={delayDuration}
    skipDelayDuration={skipDelayDuration}
    {...props}
  />
);
TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;

const TooltipPortalContainerContext = React.createContext<HTMLElement | null>(null);
const TooltipPortalContainerSetterContext = React.createContext<((container: HTMLElement | null) => void) | null>(null);

const resolveTooltipPortalContainer = (trigger: HTMLElement | null) => {
  if (!trigger) {
    return null;
  }

  const themedAncestor = trigger.closest("[data-theme]");
  return themedAncestor instanceof HTMLElement
    ? themedAncestor
    : null;
};

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T) => {
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  if (ref && typeof ref !== "string") {
    (ref as React.MutableRefObject<T>).current = value;
  }
};

/** Use when a control needs a short hint on hover or focus. */
const Tooltip = ({ children, ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) => {
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  return (
    <TooltipPortalContainerSetterContext.Provider value={setPortalContainer}>
      <TooltipPortalContainerContext.Provider value={portalContainer}>
        <TooltipPrimitive.Root {...props}>
          {children}
        </TooltipPrimitive.Root>
      </TooltipPortalContainerContext.Provider>
    </TooltipPortalContainerSetterContext.Provider>
  );
};

const TooltipTrigger = ({ ref, ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) => {
  const setPortalContainer = React.useContext(TooltipPortalContainerSetterContext);

  const handleRef = React.useCallback((node: HTMLButtonElement | null) => {
    // Composed refs detach when their identity changes. Clearing state on that
    // transient null can rerender the trigger and repeat the detach/attach loop.
    if (node) setPortalContainer?.(resolveTooltipPortalContainer(node));
    assignRef(ref as React.Ref<HTMLButtonElement | null> | undefined, node);
  }, [ref, setPortalContainer]);

  return <TooltipPrimitive.Trigger ref={handleRef} {...props} />;
};

const TooltipContent = ({ className, sideOffset = 4, ref, ...props }: React.ComponentProps<typeof TooltipPrimitive.Content>) => {
  const portalContainer = React.useContext(TooltipPortalContainerContext);

  return (
    <TooltipPrimitive.Portal container={portalContainer ?? undefined}>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-tooltip max-w-60 rounded-control border border-border-popover bg-bg-surface px-2 py-1.25 shadow-low",
          "text-xs font-[450] leading-[18px] text-text-secondary",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
};
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
